/**
 * Subdomain-based tenant resolution.
 *
 * Pattern:
 *   • Main store:  khaysdevices.store, www.khaysdevices.store,
 *                  khaysdevices.lovable.app, <preview>.lovable.app
 *   • Tenant:      <slug>.khaysdevices.store
 *
 * The `<slug>` is the first label of the hostname, and is treated as a
 * tenant slug ONLY when it is not in `RESERVED_LABELS` and the host ends
 * with a known root domain.
 *
 * Path-based fallback `/s/<slug>` is also supported during the DNS
 * wildcard rollout so existing bookmarks keep working.
 */

const ROOT_DOMAINS = [
  "khaysdevices.store",
  "khaysdevices.lovable.app",
];

const RESERVED_LABELS = new Set([
  "www",
  "khaysdevices",            // root (lovable.app)
  "id-preview--a4cf1566-0356-4718-aaa2-76f0712387d2", // dev preview prefix
  "preview",
  "admin",
  "api",
  "app",
  "auth",
  "cdn",
  "static",
  "mail",
  "notify",
]);

export interface ResolvedTenantSource {
  slug: string | null;
  /** "host" = resolved from subdomain, "path" = resolved from /s/<slug>, "none" = main store */
  source: "host" | "path" | "none";
}

export function resolveTenantFromLocation(loc: { hostname: string; pathname: string } = window.location): ResolvedTenantSource {
  // 1) Path-based fallback: /s/<slug>
  const pathMatch = /^\/s\/([a-z0-9-]+)(?:\/|$)/i.exec(loc.pathname);
  if (pathMatch) return { slug: pathMatch[1].toLowerCase(), source: "path" };

  // 2) Subdomain
  const host = loc.hostname.toLowerCase();
  // localhost / IP / unrelated host
  if (host === "localhost" || /^\d+\.\d+\.\d+\.\d+$/.test(host)) {
    return { slug: null, source: "none" };
  }
  for (const root of ROOT_DOMAINS) {
    if (host === root) return { slug: null, source: "none" };
    if (host.endsWith("." + root)) {
      const sub = host.slice(0, -("." + root).length);
      // Multi-label subs (e.g. "id-preview--...lovable.app") collapse to first label
      const firstLabel = sub.split(".")[0];
      if (!firstLabel || RESERVED_LABELS.has(firstLabel)) return { slug: null, source: "none" };
      return { slug: firstLabel, source: "host" };
    }
  }
  return { slug: null, source: "none" };
}

/**
 * Construct the canonical public URL for a given tenant slug.
 *
 * Hardened rules:
 *  • On production (`khaysdevices.store` / `www.…` / any `*.khaysdevices.store`)
 *    always return `https://<slug>.khaysdevices.store/` — the subdomain is
 *    the single source of truth.
 *  • On any other host (Lovable preview, localhost, custom dev), fall back
 *    to `${origin}/store/<slug>` so links still work in-app.
 */
export function buildTenantUrl(slug: string): string {
  const clean = String(slug || "").trim().toLowerCase();
  if (typeof window === "undefined") {
    return `https://${clean}.khaysdevices.store/`;
  }
  const host = window.location.hostname.toLowerCase();
  if (host === "khaysdevices.store" || host === "www.khaysdevices.store" || host.endsWith(".khaysdevices.store")) {
    return `https://${clean}.khaysdevices.store/`;
  }
  return `${window.location.origin}/store/${clean}`;
}
